import React, { useCallback, useEffect, useState } from 'react';


const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

async function responseMessage(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  return payload.detail || payload.error || fallback;
}

export default function CreditorMailingMatrixPanel({ caseId }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrieving, setRetrieving] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_URL}/case/${caseId}/creditor-mailing-matrices/`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not load mailing matrices.'));
      setContext(await response.json());
    } catch (loadError) {
      setError(loadError.message || 'Could not load mailing matrices.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
  }, [preview]);

  const retrieve = async () => {
    setRetrieving(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${API_URL}/case/${caseId}/creditor-mailing-matrices/`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) throw new Error(await responseMessage(response, 'The court mailing matrix could not be retrieved.'));
      const created = await response.json();
      setContext(current => ({
        ...(current || {}),
        latest: created,
        matrices: [
          created,
          ...((current?.matrices || []).filter(item => item.id !== created.id)),
        ].slice(0, 10),
      }));
    } catch (retrieveError) {
      setError(retrieveError.message || 'The court mailing matrix could not be retrieved.');
    } finally {
      setRetrieving(false);
    }
  };

  const openMatrix = async matrix => {
    setOpeningId(matrix.id);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}${matrix.pdf_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(await responseMessage(response, 'Could not open the mailing matrix.'));
      const objectUrl = URL.createObjectURL(await response.blob());
      setPreview({ url: objectUrl, filename: matrix.filename || 'Creditor mailing matrix.pdf' });
    } catch (openError) {
      setError(openError.message || 'Could not open the mailing matrix.');
    } finally {
      setOpeningId(null);
    }
  };

  const latest = context?.latest;

  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6 shadow">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Court Creditor Mailing Matrix</h3>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Pull the court&apos;s current mailing list, including scheduled creditors and creditors who filed claims. The saved addresses can be used for amended-document service and envelopes.
          </p>
        </div>
        <button
          type="button"
          onClick={retrieve}
          disabled={loading || retrieving || !context?.can_retrieve}
          className="shrink-0 rounded-md bg-indigo-700 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-800 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {retrieving ? 'Retrieving from CM/ECF…' : latest ? 'Pull Current Matrix' : 'Pull Creditor Matrix'}
        </button>
      </div>

      {error && <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-800">{error}</p>}
      {loading ? (
        <p className="mt-4 text-sm text-gray-500">Loading saved matrices…</p>
      ) : !context?.can_retrieve ? (
        <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Save the court case number before retrieving a mailing matrix.
        </p>
      ) : latest ? (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <div className="font-semibold text-emerald-900">Latest court matrix</div>
          <div className="mt-1 text-sm text-emerald-900">
            {latest.recipient_count} creditor address{latest.recipient_count === 1 ? '' : 'es'} · retrieved {new Date(latest.retrieved_at).toLocaleString()}
          </div>
          <button
            type="button"
            onClick={() => openMatrix(latest)}
            disabled={openingId === latest.id}
            className="mt-3 rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
          >
            {openingId === latest.id ? 'Opening…' : 'View Current Matrix PDF'}
          </button>
        </div>
      ) : (
        <p className="mt-4 text-sm text-gray-500">No creditor mailing matrix has been saved for this case.</p>
      )}

      {(context?.matrices || []).length > 1 && (
        <details className="mt-4 rounded-md border border-gray-200 p-3">
          <summary className="cursor-pointer text-sm font-semibold text-gray-800">
            Earlier matrices ({context.matrices.length - 1})
          </summary>
          <div className="mt-3 space-y-2">
            {context.matrices.slice(1).map(matrix => (
              <div key={matrix.id} className="flex flex-wrap items-center justify-between gap-2 rounded border border-gray-100 px-3 py-2 text-sm">
                <span>{new Date(matrix.retrieved_at).toLocaleString()} · {matrix.recipient_count} addresses</span>
                <button type="button" onClick={() => openMatrix(matrix)} disabled={openingId === matrix.id} className="font-semibold text-indigo-700 hover:underline disabled:opacity-50">
                  {openingId === matrix.id ? 'Opening…' : 'View'}
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      {preview && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3 sm:p-6">
          <div className="flex h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b px-4 py-3">
              <h2 className="min-w-0 truncate text-lg font-bold text-gray-900">{preview.filename}</h2>
              <button type="button" onClick={() => setPreview(null)} className="rounded bg-gray-800 px-3 py-2 text-sm font-semibold text-white hover:bg-gray-900">Close</button>
            </div>
            <iframe src={preview.url} title={preview.filename} className="min-h-0 flex-1 bg-gray-100" />
          </div>
        </div>
      )}
    </section>
  );
}
